import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";

const COLLECTION_RECEITAS = "financeiro_receitas";
const COLLECTION_CONTAS_PAGAR = "financeiro_contas_pagar";

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function inRange(date, start, end) {
  if (!date || !start || !end) return false;
  return date >= start && date <= end;
}

function firstDayOfMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [y, m] = yyyyMm.split("-").map(Number);
  return new Date(y, m - 1, 1, 0, 0, 0, 0);
}

function lastDayOfMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [y, m] = yyyyMm.split("-").map(Number);
  return new Date(y, m, 0, 23, 59, 59, 999);
}

function statusUpper(value) {
  return String(value || "").trim().toUpperCase();
}

function groupSum(items, keyGetter, valueGetter) {
  const map = new Map();
  for (const item of items) {
    const key = keyGetter(item) || "(Não informado)";
    const prev = map.get(key) || 0;
    map.set(key, prev + Number(valueGetter(item) || 0));
  }
  return Array.from(map.entries())
    .map(([chave, valor]) => ({ chave, valor }))
    .sort((a, b) => b.valor - a.valor);
}

export default function FinanceiroRelatoriosPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();

  const now = new Date();
  const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [mesInicio, setMesInicio] = useState(mesAtual);
  const [mesFim, setMesFim] = useState(mesAtual);
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const [snapReceitas, snapDespesas] = await Promise.all([
          getDocs(query(collection(db, COLLECTION_RECEITAS), where("grupoId", "==", usuario.grupoId))),
          getDocs(query(collection(db, COLLECTION_CONTAS_PAGAR), where("grupoId", "==", usuario.grupoId))),
        ]);

        let listaReceitas = snapReceitas.docs.map((d) => ({ id: d.id, ...d.data() }));
        let listaDespesas = snapDespesas.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (restringirEmpresa && empresaEscopoId) {
          const empresaRef = String(empresaEscopoId);
          listaReceitas = listaReceitas.filter((item) => String(item?.empresaId || "") === empresaRef);
          listaDespesas = listaDespesas.filter((item) => String(item?.empresaId || "") === empresaRef);
        }

        setReceitas(listaReceitas);
        setDespesas(listaDespesas);
      } catch (e) {
        console.error("Erro ao carregar relatórios financeiros:", e);
        setErro("Erro ao carregar dados para relatórios financeiros.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, restringirEmpresa, empresaEscopoId]);

  const inicio = useMemo(() => firstDayOfMonth(mesInicio), [mesInicio]);
  const fim = useMemo(() => lastDayOfMonth(mesFim), [mesFim]);

  const receitasPeriodo = useMemo(
    () => receitas.filter((r) => inRange(parseDate(r.vencimento || r.competencia || r.createdAt), inicio, fim)),
    [receitas, inicio, fim]
  );

  const despesasPeriodo = useMemo(
    () => despesas.filter((d) => inRange(parseDate(d.vencimento || d.competencia || d.createdAt), inicio, fim)),
    [despesas, inicio, fim]
  );

  const totalReceitas = useMemo(
    () =>
      receitasPeriodo.reduce((acc, item) => {
        const previsto = Number(item.valorPrevisto || 0) - Number(item.desconto || 0);
        return acc + previsto;
      }, 0),
    [receitasPeriodo]
  );

  const totalDespesas = useMemo(
    () => despesasPeriodo.reduce((acc, item) => acc + Number(item.valorFinal || 0), 0),
    [despesasPeriodo]
  );

  const saldo = totalReceitas - totalDespesas;

  const receitasPorStatus = useMemo(
    () =>
      groupSum(
        receitasPeriodo,
        (item) => statusUpper(item.status),
        (item) => Number(item.valorPrevisto || 0) - Number(item.desconto || 0)
      ),
    [receitasPeriodo]
  );

  const despesasPorStatus = useMemo(
    () =>
      groupSum(
        despesasPeriodo,
        (item) => statusUpper(item.status),
        (item) => Number(item.valorFinal || 0)
      ),
    [despesasPeriodo]
  );

  const despesasPorCategoria = useMemo(
    () =>
      groupSum(
        despesasPeriodo,
        (item) => String(item.categoria || "").trim(),
        (item) => Number(item.valorFinal || 0)
      ).slice(0, 10),
    [despesasPeriodo]
  );

  const receitasPorCategoria = useMemo(
    () =>
      groupSum(
        receitasPeriodo,
        (item) => String(item.categoria || "").trim(),
        (item) => Number(item.valorPrevisto || 0) - Number(item.desconto || 0)
      ).slice(0, 10),
    [receitasPeriodo]
  );

  const topClientesReceita = useMemo(
    () =>
      groupSum(
        receitasPeriodo,
        (item) => String(item.clienteOrigem || item.clienteNome || "").trim(),
        (item) => Number(item.valorPrevisto || 0) - Number(item.desconto || 0)
      ).slice(0, 10),
    [receitasPeriodo]
  );

  return (
    <LayoutDesktop tema={tema} titulo="Financeiro" subtitulo="Gerenciar / Relatórios">
      <section className="pc-card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Relatórios Financeiros</h3>
        <p style={{ marginTop: 8, opacity: 0.9 }}>
          Consolidados por período com base em Receitas e Contas a Pagar.
        </p>

        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label className="pc-field" style={{ marginBottom: 0 }}>
            <span>De (mês)</span>
            <input type="month" className="pc-input" value={mesInicio} onChange={(e) => setMesInicio(e.target.value)} />
          </label>

          <label className="pc-field" style={{ marginBottom: 0 }}>
            <span>Até (mês)</span>
            <input type="month" className="pc-input" value={mesFim} onChange={(e) => setMesFim(e.target.value)} />
          </label>
        </div>
      </section>

      {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

      <section className="pc-card" style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Total receitas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(totalReceitas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Total despesas</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(totalDespesas)}</div>
          </div>
          <div className="pc-card" style={{ margin: 0 }}>
            <strong>Saldo do período</strong>
            <div style={{ marginTop: 6, fontSize: 22 }}>R$ {formatMoney(saldo)}</div>
          </div>
        </div>
      </section>

      <section className="pc-card pc-table-card" style={{ marginBottom: 14 }}>
        <header className="pc-table-header">
          <div><h3>Receitas por status</h3></div>
        </header>
        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead><tr><th>Status</th><th>Valor</th></tr></thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Carregando...</td></tr>
              ) : receitasPorStatus.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Sem dados no período.</td></tr>
              ) : (
                receitasPorStatus.map((row) => (
                  <tr key={row.chave}><td>{row.chave}</td><td>R$ {formatMoney(row.valor)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pc-card pc-table-card" style={{ marginBottom: 14 }}>
        <header className="pc-table-header">
          <div><h3>Despesas por status</h3></div>
        </header>
        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead><tr><th>Status</th><th>Valor</th></tr></thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Carregando...</td></tr>
              ) : despesasPorStatus.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Sem dados no período.</td></tr>
              ) : (
                despesasPorStatus.map((row) => (
                  <tr key={row.chave}><td>{row.chave}</td><td>R$ {formatMoney(row.valor)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pc-card pc-table-card" style={{ marginBottom: 14 }}>
        <header className="pc-table-header">
          <div><h3>Top categorias de despesas</h3></div>
        </header>
        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead><tr><th>Categoria</th><th>Valor</th></tr></thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Carregando...</td></tr>
              ) : despesasPorCategoria.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Sem dados no período.</td></tr>
              ) : (
                despesasPorCategoria.map((row) => (
                  <tr key={row.chave}><td>{row.chave}</td><td>R$ {formatMoney(row.valor)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pc-card pc-table-card" style={{ marginBottom: 14 }}>
        <header className="pc-table-header">
          <div><h3>Top categorias de receitas</h3></div>
        </header>
        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead><tr><th>Categoria</th><th>Valor</th></tr></thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Carregando...</td></tr>
              ) : receitasPorCategoria.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Sem dados no período.</td></tr>
              ) : (
                receitasPorCategoria.map((row) => (
                  <tr key={row.chave}><td>{row.chave}</td><td>R$ {formatMoney(row.valor)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div><h3>Top clientes (receitas)</h3></div>
        </header>
        <div className="pc-table-wrapper">
          <table className="pc-table">
            <thead><tr><th>Cliente</th><th>Valor</th></tr></thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Carregando...</td></tr>
              ) : topClientesReceita.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 16 }}>Sem dados no período.</td></tr>
              ) : (
                topClientesReceita.map((row) => (
                  <tr key={row.chave}><td>{row.chave}</td><td>R$ {formatMoney(row.valor)}</td></tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
